// AI聊天內容追蹤和分析工具
import { logFirebaseEvent } from './firebaseAnalytics';
import { trackEvent } from './analytics';

// 聊天內容分析類
export class ChatAnalytics {
  private static instance: ChatAnalytics;
  private chatSessions: Map<string, ChatSession> = new Map();

  static getInstance(): ChatAnalytics {
    if (!ChatAnalytics.instance) {
      ChatAnalytics.instance = new ChatAnalytics();
    }
    return ChatAnalytics.instance;
  }

  // 開始新的聊天會話
  startChatSession(sessionId: string, userInfo?: any) {
    const session: ChatSession = {
      sessionId,
      startTime: new Date(),
      messages: [],
      userInfo: userInfo || {},
      analytics: {
        totalMessages: 0,
        userMessages: 0,
        aiMessages: 0,
        averageResponseTime: 0,
        topics: new Set(),
        sentiment: 'neutral',
        userSatisfaction: null,
      }
    };
    
    this.chatSessions.set(sessionId, session);
    
    // 追蹤聊天會話開始
    this.trackChatEvent('chat_session_started', {
      session_id: sessionId,
      timestamp: new Date().toISOString(),
    });
  }

  // 追蹤用戶消息
  trackUserMessage(sessionId: string, message: string, messageId: string) {
    const session = this.chatSessions.get(sessionId);
    if (!session) return;

    const userMessage: ChatMessage = {
      id: messageId,
      content: message,
      sender: 'user',
      timestamp: new Date(),
      metadata: {
        length: message.length,
        wordCount: message.split(' ').length,
        hasQuestion: message.includes('?'),
        topics: this.extractTopics(message),
        sentiment: this.analyzeSentiment(message),
      }
    };

    session.messages.push(userMessage);
    session.analytics.userMessages++;
    session.analytics.totalMessages++;

    // 更新主題
    userMessage.metadata.topics.forEach(topic => {
      session.analytics.topics.add(topic);
    });

    // 追蹤用戶消息
    this.trackChatEvent('user_message_sent', {
      session_id: sessionId,
      message_id: messageId,
      message_length: message.length,
      word_count: userMessage.metadata.wordCount,
      has_question: userMessage.metadata.hasQuestion,
      topics: Array.from(userMessage.metadata.topics),
      sentiment: userMessage.metadata.sentiment,
      timestamp: new Date().toISOString(),
    });
  }

  // 追蹤AI回應
  trackAIResponse(sessionId: string, response: string, messageId: string, responseTime?: number) {
    const session = this.chatSessions.get(sessionId);
    if (!session) return;

    const aiMessage: ChatMessage = {
      id: messageId,
      content: response,
      sender: 'ai',
      timestamp: new Date(),
      metadata: {
        length: response.length,
        wordCount: response.split(' ').length,
        responseTime: responseTime || 0,
        topics: this.extractTopics(response),
        sentiment: this.analyzeSentiment(response),
        helpfulness: null, // 可以後續添加用戶評分
      }
    };

    session.messages.push(aiMessage);
    session.analytics.aiMessages++;
    session.analytics.totalMessages++;

    // 更新平均回應時間
    if (responseTime) {
      const totalResponseTime = session.analytics.averageResponseTime * (session.analytics.aiMessages - 1) + responseTime;
      session.analytics.averageResponseTime = totalResponseTime / session.analytics.aiMessages;
    }

    // 追蹤AI回應
    this.trackChatEvent('ai_response_sent', {
      session_id: sessionId,
      message_id: messageId,
      response_length: response.length,
      word_count: aiMessage.metadata.wordCount,
      response_time: responseTime || 0,
      topics: Array.from(aiMessage.metadata.topics),
      sentiment: aiMessage.metadata.sentiment,
      timestamp: new Date().toISOString(),
    });
  }

  // 結束聊天會話
  endChatSession(sessionId: string) {
    const session = this.chatSessions.get(sessionId);
    if (!session) return;

    const sessionDuration = new Date().getTime() - session.startTime.getTime();
    
    // 追蹤會話結束
    this.trackChatEvent('chat_session_ended', {
      session_id: sessionId,
      session_duration: sessionDuration,
      total_messages: session.analytics.totalMessages,
      user_messages: session.analytics.userMessages,
      ai_messages: session.analytics.aiMessages,
      average_response_time: session.analytics.averageResponseTime,
      topics_discussed: Array.from(session.analytics.topics),
      timestamp: new Date().toISOString(),
    });

    // 保存會話數據到Firestore（可選）
    this.saveSessionToFirestore(session);

    // 從內存中移除會話
    this.chatSessions.delete(sessionId);
  }

  // 分析聊天內容
  analyzeChatContent(sessionId: string) {
    const session = this.chatSessions.get(sessionId);
    if (!session) return null;

    const analysis = {
      sessionId: session.sessionId,
      duration: new Date().getTime() - session.startTime.getTime(),
      messageCount: session.messages.length,
      topics: Array.from(session.analytics.topics),
      sentiment: this.calculateOverallSentiment(session.messages),
      userEngagement: this.calculateUserEngagement(session.messages),
      commonQuestions: this.extractCommonQuestions(session.messages),
      responseQuality: this.assessResponseQuality(session.messages),
    };

    return analysis;
  }

  // 獲取所有聊天會話的統計數據
  getAllChatStatistics() {
    const allSessions = Array.from(this.chatSessions.values());
    
    return {
      totalSessions: allSessions.length,
      totalMessages: allSessions.reduce((sum, session) => sum + session.analytics.totalMessages, 0),
      averageSessionDuration: allSessions.reduce((sum, session) => {
        return sum + (new Date().getTime() - session.startTime.getTime());
      }, 0) / allSessions.length,
      mostCommonTopics: this.getMostCommonTopics(allSessions),
      averageResponseTime: allSessions.reduce((sum, session) => sum + session.analytics.averageResponseTime, 0) / allSessions.length,
      userSatisfaction: this.calculateOverallSatisfaction(allSessions),
    };
  }

  // 私有方法：提取主題
  private extractTopics(text: string): Set<string> {
    const topics = new Set<string>();
    const lowerText = text.toLowerCase();

    // 保險相關主題關鍵詞
    const topicKeywords = {
      'health_insurance': ['health', 'medical', 'hospital', 'doctor', 'healthcare', '健康', '醫療', '醫院'],
      'life_insurance': ['life', 'death', 'beneficiary', 'life insurance', '人壽', '身故', '受益人'],
      'auto_insurance': ['car', 'auto', 'vehicle', 'accident', '車險', '汽車', '事故'],
      'home_insurance': ['home', 'house', 'property', 'fire', '房屋', '財產', '火災'],
      'travel_insurance': ['travel', 'trip', 'vacation', '旅遊', '旅行', '假期'],
      'policy_management': ['policy', 'premium', 'coverage', 'claim', '保單', '保費', '理賠'],
      'ai_assistance': ['help', 'assist', 'support', 'question', '幫助', '協助', '問題'],
      'general_inquiry': ['what', 'how', 'when', 'where', 'why', '什麼', '如何', '何時'],
    };

    Object.entries(topicKeywords).forEach(([topic, keywords]) => {
      if (keywords.some(keyword => lowerText.includes(keyword))) {
        topics.add(topic);
      }
    });

    return topics;
  }

  // 私有方法：分析情感
  private analyzeSentiment(text: string): 'positive' | 'negative' | 'neutral' {
    const lowerText = text.toLowerCase();
    
    const positiveWords = ['good', 'great', 'excellent', 'helpful', 'thanks', 'thank you', '好', '很好', '謝謝', '感謝'];
    const negativeWords = ['bad', 'terrible', 'wrong', 'error', 'problem', 'issue', '壞', '糟糕', '錯誤', '問題'];

    const positiveCount = positiveWords.filter(word => lowerText.includes(word)).length;
    const negativeCount = negativeWords.filter(word => lowerText.includes(word)).length;

    if (positiveCount > negativeCount) return 'positive';
    if (negativeCount > positiveCount) return 'negative';
    return 'neutral';
  }

  // 私有方法：計算整體情感
  private calculateOverallSentiment(messages: ChatMessage[]): 'positive' | 'negative' | 'neutral' {
    const sentiments = messages.map(msg => msg.metadata.sentiment);
    const positiveCount = sentiments.filter(s => s === 'positive').length;
    const negativeCount = sentiments.filter(s => s === 'negative').length;

    if (positiveCount > negativeCount) return 'positive';
    if (negativeCount > positiveCount) return 'negative';
    return 'neutral';
  }

  // 私有方法：計算用戶參與度
  private calculateUserEngagement(messages: ChatMessage[]): number {
    const userMessages = messages.filter(msg => msg.sender === 'user');
    const avgMessageLength = userMessages.reduce((sum, msg) => sum + msg.metadata.length, 0) / userMessages.length;
    const questionCount = userMessages.filter(msg => msg.metadata.hasQuestion).length;
    
    // 參與度評分 (0-100)
    const lengthScore = Math.min(avgMessageLength / 50 * 30, 30); // 消息長度評分
    const questionScore = Math.min(questionCount / userMessages.length * 40, 40); // 問題數量評分
    const interactionScore = Math.min(userMessages.length * 2, 30); // 互動次數評分

    return Math.round(lengthScore + questionScore + interactionScore);
  }

  // 私有方法：提取常見問題
  private extractCommonQuestions(messages: ChatMessage[]): string[] {
    const questions = messages
      .filter(msg => msg.sender === 'user' && msg.metadata.hasQuestion)
      .map(msg => msg.content)
      .slice(0, 5); // 最多返回5個問題

    return questions;
  }

  // 私有方法：評估回應質量
  private assessResponseQuality(messages: ChatMessage[]): number {
    const aiMessages = messages.filter(msg => msg.sender === 'ai');
    const avgResponseLength = aiMessages.reduce((sum, msg) => sum + msg.metadata.length, 0) / aiMessages.length;
    const avgResponseTime = aiMessages.reduce((sum, msg) => sum + (msg.metadata.responseTime || 0), 0) / aiMessages.length;

    // 質量評分 (0-100)
    const lengthScore = Math.min(avgResponseLength / 200 * 40, 40); // 回應長度評分
    const timeScore = Math.max(0, 30 - avgResponseTime / 1000); // 回應時間評分（越快越好）
    const consistencyScore = 30; // 一致性評分（可以根據實際情況調整）

    return Math.round(lengthScore + timeScore + consistencyScore);
  }

  // 私有方法：獲取最常見主題
  private getMostCommonTopics(sessions: ChatSession[]): Array<{topic: string, count: number}> {
    const topicCounts = new Map<string, number>();
    
    sessions.forEach(session => {
      session.analytics.topics.forEach(topic => {
        topicCounts.set(topic, (topicCounts.get(topic) || 0) + 1);
      });
    });

    return Array.from(topicCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([topic, count]) => ({ topic, count }));
  }

  // 私有方法：計算整體滿意度
  private calculateOverallSatisfaction(sessions: ChatSession[]): number {
    // 基於會話持續時間、消息數量和主題覆蓋度計算滿意度
    const avgDuration = sessions.reduce((sum, session) => {
      return sum + (new Date().getTime() - session.startTime.getTime());
    }, 0) / sessions.length;

    const avgMessages = sessions.reduce((sum, session) => sum + session.analytics.totalMessages, 0) / sessions.length;
    const avgTopics = sessions.reduce((sum, session) => sum + session.analytics.topics.size, 0) / sessions.length;

    // 滿意度評分 (0-100)
    const durationScore = Math.min(avgDuration / 300000 * 30, 30); // 5分鐘為滿分
    const messageScore = Math.min(avgMessages / 10 * 40, 40); // 10條消息為滿分
    const topicScore = Math.min(avgTopics / 5 * 30, 30); // 5個主題為滿分

    return Math.round(durationScore + messageScore + topicScore);
  }

  // 私有方法：追蹤聊天事件
  private trackChatEvent(eventName: string, parameters: any) {
    // GA4 追蹤
    trackEvent(`chat_${eventName}`, {
      event_category: 'AI Chat',
      ...parameters,
    });

    // Firebase 追蹤
    logFirebaseEvent(`chat_${eventName}`, parameters);
  }

  // 私有方法：保存會話到Firestore
  private async saveSessionToFirestore(session: ChatSession) {
    try {
      // 這裡可以實現將聊天數據保存到Firestore
      // 注意：需要確保隱私合規，可能需要匿名化處理
      console.log('Chat session data:', {
        sessionId: session.sessionId,
        messageCount: session.messages.length,
        topics: Array.from(session.analytics.topics),
        duration: new Date().getTime() - session.startTime.getTime(),
      });
    } catch (error) {
      console.error('Error saving chat session to Firestore:', error);
    }
  }
}

// 類型定義
interface ChatSession {
  sessionId: string;
  startTime: Date;
  messages: ChatMessage[];
  userInfo: any;
  analytics: {
    totalMessages: number;
    userMessages: number;
    aiMessages: number;
    averageResponseTime: number;
    topics: Set<string>;
    sentiment: 'positive' | 'negative' | 'neutral';
    userSatisfaction: number | null;
  };
}

interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  metadata: {
    length: number;
    wordCount: number;
    hasQuestion?: boolean;
    responseTime?: number;
    topics: Set<string>;
    sentiment: 'positive' | 'negative' | 'neutral';
    helpfulness?: number | null;
  };
}

// 導出實例
export const chatAnalytics = ChatAnalytics.getInstance();

// 導出便捷函數
export const trackChatMessage = (sessionId: string, message: string, messageId: string, sender: 'user' | 'ai', responseTime?: number) => {
  if (sender === 'user') {
    chatAnalytics.trackUserMessage(sessionId, message, messageId);
  } else {
    chatAnalytics.trackAIResponse(sessionId, message, messageId, responseTime);
  }
};

export const startChatSession = (sessionId: string, userInfo?: any) => {
  chatAnalytics.startChatSession(sessionId, userInfo);
};

export const endChatSession = (sessionId: string) => {
  chatAnalytics.endChatSession(sessionId);
};

export const getChatStatistics = () => {
  return chatAnalytics.getAllChatStatistics();
};

export default chatAnalytics;
